"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/components/ui/cn";
import {
  clearNetworkEntries,
  getNetworkEntries,
  getServerNetworkEntries,
  installNetworkDebug,
  isNetworkDebugEnabled,
  setErudaEnabled,
  setNetworkDebugEnabled,
  subscribeNetworkDebugEnabled,
  subscribeNetworkEntries,
  type NetEntry,
} from "@/lib/networkDebug";

/** Whether the network debug bar is switched on (header menu or `?netdebug=1`). */
export function useNetworkDebugEnabled(): boolean {
  return useSyncExternalStore(
    subscribeNetworkDebugEnabled,
    isNetworkDebugEnabled,
    () => false
  );
}

type Filter = "all" | "api" | "assets" | "errors";

const isApi = (e: NetEntry) => e.kind === "fetch" || e.kind === "xmlhttprequest";
const isFailed = (e: NetEntry) => !!e.error || (e.status !== null && e.status >= 400);
const isPending = (e: NetEntry) => e.duration === null && !e.error;

function formatMs(ms: number | null): string {
  if (ms === null) return "…";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** Short label: backend proxy calls without their prefix, assets by file name. */
function shortName(entry: NetEntry): string {
  try {
    const u = new URL(entry.url);
    const backend = u.pathname.indexOf("/api/backend/");
    if (backend !== -1) {
      return decodeURIComponent(u.pathname.slice(backend + "/api/backend/".length)) + u.search;
    }
    if (u.origin !== window.location.origin) return u.host + u.pathname;
    if (entry.kind === "fetch" || entry.kind === "document") return u.pathname + u.search;
    return u.pathname.split("/").pop() || u.pathname;
  } catch {
    return entry.url;
  }
}

function timingSummary(entry: NetEntry): string {
  return entry.serverTiming
    .map((s) => `${s.name} ${Math.round(s.dur)}${s.desc ? ` (${s.desc})` : ""}`)
    .join(" · ");
}

function statusClass(entry: NetEntry): string {
  if (isFailed(entry)) return "text-danger";
  if (isPending(entry)) return "text-accent";
  return "text-positive";
}

/**
 * Debug strip under the app header listing every load's state — in flight,
 * done, failed — with a full-screen log (status, time, size, server-timing
 * breakdown, waterfall) behind a tap. For debugging on phones, where there
 * are no devtools. Off unless enabled from the header menu or `?netdebug=1`;
 * developer-facing, so its text is not localised.
 */
export default function NetworkDebugBar() {
  const enabled = useNetworkDebugEnabled();

  // `?netdebug=1` / `?netdebug=0` switch it from a link, handy on a phone.
  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get("netdebug");
    if (flag === "1" || flag === "0") setNetworkDebugEnabled(flag === "1");
  }, []);

  useEffect(() => {
    if (enabled) installNetworkDebug();
  }, [enabled]);

  // Follow runtime toggles only. Hydration renders `false` first (server
  // snapshot) — acting on that would tear down the Eruda layout.tsx loaded.
  const erudaSynced = useRef(false);
  useEffect(() => {
    if (!erudaSynced.current) {
      erudaSynced.current = true;
      if (!enabled) return;
    }
    setErudaEnabled(enabled);
  }, [enabled]);

  if (!enabled) return null;
  return <NetworkDebugStrip />;
}

function NetworkDebugStrip() {
  const entries = useSyncExternalStore(
    subscribeNetworkEntries,
    getNetworkEntries,
    getServerNetworkEntries
  );
  const [open, setOpen] = useState(false);

  const stats = useMemo(() => {
    let pending = 0;
    let failed = 0;
    let bytes = 0;
    let slowest: NetEntry | null = null;
    for (const e of entries) {
      if (isPending(e)) pending++;
      if (isFailed(e)) failed++;
      bytes += e.size ?? 0;
      if (e.duration !== null && (!slowest || e.duration > (slowest.duration ?? 0))) {
        slowest = e;
      }
    }
    return { pending, failed, bytes, slowest, done: entries.length - pending };
  }, [entries]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-[44px] w-full items-center gap-3 overflow-hidden border-b border-border bg-surface-muted px-4 text-left font-mono text-xs tabular-nums text-muted"
      >
        <span className={cn("shrink-0", stats.pending ? "animate-pulse text-accent" : "")}>
          ⟳ {stats.pending}
        </span>
        <span className="shrink-0 text-positive">✓ {stats.done}</span>
        <span className={cn("shrink-0", stats.failed ? "font-semibold text-danger" : "")}>
          ✗ {stats.failed}
        </span>
        <span className="shrink-0">Σ {formatBytes(stats.bytes)}</span>
        {stats.slowest && (
          <span className="min-w-0 truncate">
            max {formatMs(stats.slowest.duration)} {shortName(stats.slowest)}
          </span>
        )}
      </button>
      {/* Portalled to <body>: an ancestor with a backdrop-filter/transform
          would otherwise become the containing block of `position: fixed`
          and squeeze the full-screen sheet into its own box. */}
      {open &&
        createPortal(
          <NetworkLogSheet entries={entries} onClose={() => setOpen(false)} />,
          document.body
        )}
    </>
  );
}

function NetworkLogSheet({
  entries,
  onClose,
}: {
  entries: NetEntry[];
  onClose: () => void;
}) {
  const [filter, setFilter] = useState<Filter>("api");
  const [copied, setCopied] = useState(false);

  const visible = useMemo(() => {
    const list = entries.filter((e) =>
      filter === "all"
        ? true
        : filter === "api"
          ? isApi(e)
          : filter === "assets"
            ? !isApi(e)
            : isFailed(e)
    );
    return [...list].sort((a, b) => a.startedAt - b.startedAt);
  }, [entries, filter]);

  // Waterfall scale: from the first start to the latest finish (or now, for
  // anything still in flight) among the visible rows.
  // A ticking clock so in-flight bars grow while the sheet is open.
  const [now, setNow] = useState(0);
  useEffect(() => {
    const tick = () => setNow(performance.now());
    const id = window.setInterval(tick, 250);
    tick();
    return () => window.clearInterval(id);
  }, []);

  const [t0, span] = useMemo(() => {
    if (!visible.length) return [0, 1];
    const start = Math.min(...visible.map((e) => e.startedAt));
    const end = Math.max(...visible.map((e) => e.startedAt + (e.duration ?? now - e.startedAt)));
    return [start, Math.max(end - start, 1)];
  }, [visible, now]);

  // Copies every recorded entry, not just the current filter.
  async function copy() {
    const text = [...entries]
      .sort((a, b) => a.startedAt - b.startedAt)
      .map((e) =>
        [
          `+${Math.round(e.startedAt)}ms`,
          e.method,
          e.error ?? e.status ?? "…",
          formatMs(e.duration),
          formatBytes(e.size),
          e.url,
          timingSummary(e),
        ]
          .filter(Boolean)
          .join("  ")
      )
      .join("\n");
    try {
      await navigator.clipboard.writeText(
        `${window.location.href}\n${navigator.userAgent}\n${entries.length} entries\n\n${text}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context, e.g. http on the LAN).
    }
  }

  const filters: { key: Filter; label: string }[] = [
    { key: "api", label: "API" },
    { key: "assets", label: "Assets" },
    { key: "errors", label: "Errors" },
    { key: "all", label: "All" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground">
      <div className="flex items-center gap-2 border-b border-border bg-surface px-3 py-2">
        <span className="mr-auto text-sm font-semibold">Network ({visible.length})</span>
        <button
          type="button"
          onClick={copy}
          className="min-h-[44px] rounded-md px-3 text-sm text-accent"
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={clearNetworkEntries}
          className="min-h-[44px] rounded-md px-3 text-sm text-muted"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => setNetworkDebugEnabled(false)}
          className="min-h-[44px] rounded-md px-3 text-sm text-danger"
        >
          Off
        </button>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-lg"
        >
          ×
        </button>
      </div>
      <div className="flex gap-1 border-b border-border px-3 py-2">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              "min-h-[36px] rounded-full px-3 text-xs font-medium",
              filter === f.key ? "bg-accent text-white" : "bg-surface-muted text-muted"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      <ul className="flex-1 divide-y divide-border overflow-y-auto overscroll-contain">
        {visible.map((e) => {
          const offset = ((e.startedAt - t0) / span) * 100;
          const width = Math.max(
            ((e.duration ?? Math.max(now - e.startedAt, 0)) / span) * 100,
            0.5
          );
          return (
            <li key={e.id} className="px-3 py-2">
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate font-mono text-xs" title={e.url}>
                  {e.method !== "GET" && (
                    <span className="mr-1 font-semibold">{e.method}</span>
                  )}
                  {shortName(e)}
                </span>
                <span className={cn("shrink-0 font-mono text-xs font-semibold", statusClass(e))}>
                  {e.error ?? e.status ?? (isPending(e) ? "…" : "—")}
                </span>
                <span className="w-16 shrink-0 text-right font-mono text-xs tabular-nums">
                  {formatMs(e.duration)}
                </span>
              </div>
              <div className="mt-0.5 flex gap-2 font-mono text-[11px] text-muted">
                <span className="shrink-0">{e.kind}</span>
                <span className="shrink-0">{formatBytes(e.size)}</span>
                <span className="shrink-0">+{Math.round(e.startedAt)}ms</span>
              </div>
              {/* Own line, wrapping: on a phone it never fit beside the rest. */}
              {(e.serverTiming.length > 0 || isApi(e)) && (
                <div className="mt-0.5 break-words font-mono text-[11px] text-accent">
                  {e.serverTiming.length
                    ? timingSummary(e)
                    : isPending(e)
                      ? ""
                      : "no server-timing header"}
                </div>
              )}
              <div className="relative mt-1 h-1 rounded-full bg-surface-muted">
                <div
                  className={cn(
                    "absolute h-1 rounded-full",
                    isFailed(e) ? "bg-danger" : isPending(e) ? "animate-pulse bg-accent" : "bg-accent/70"
                  )}
                  style={{ left: `${offset}%`, width: `${Math.min(width, 100 - offset)}%` }}
                />
              </div>
            </li>
          );
        })}
        {!visible.length && (
          <li className="px-3 py-8 text-center text-sm text-muted">Nothing recorded yet.</li>
        )}
      </ul>
    </div>
  );
}
